/* ==========================================================================\n   RAREFORM MOTION SYSTEM — PHASE 1\n   Homepage scroll reveals + restrained global interactions\n   File: assets/rareform-motion.js\n   ========================================================================== */

(() => {
  'use strict';

  const documentElement = document.documentElement;
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const EXCLUDED_SELECTOR = [
    '[data-rf-motion="off"]',
    '[hidden]',
    '[aria-hidden="true"]',
    'template',
    'script',
    'style',
    'noscript',
    'dialog',
    '[role="dialog"]',
    'search-modal',
    'quick-add-modal',
    '.modal',
    '.drawer',
    '.predictive-search',
    '.rfp-dock',
    '[data-rfp-mobile-dock]',
    '.rfp-hero',
    '[data-rfp-section]'
  ].join(',');

  const DECORATIVE_CLASS_PATTERN = /(?:^|[-_])(ambient|background|bg|glow|noise|overlay|vignette|particle|light|marquee|ticker|ribbon|track|spacer|separator|divider)(?:$|[-_])/i;
  const NATIVE_MOTION_SECTION_PATTERN = /(?:hero|marquee|ticker|ribbon|announcement|slideshow)/i;
  const MEDIA_CLASS_PATTERN = /(?:media|visual|image|picture|stage|product|gallery|card)/i;
  const STRUCTURAL_CLASS_PATTERN = /(?:shell|inner|container|wrapper|wrap|grid|content|layout)/i;

  let observer = null;

  const isHTMLElement = (value) => value instanceof HTMLElement;

  const classText = (element) => {
    if (!isHTMLElement(element)) return '';
    return typeof element.className === 'string' ? element.className : '';
  };

  const isExcluded = (element) => {
    if (!isHTMLElement(element)) return true;
    return Boolean(element.closest(EXCLUDED_SELECTOR));
  };

  const isVisibleInLayout = (element) => {
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  };

  const hasUnsafeNativeMotion = (element) => {
    const style = window.getComputedStyle(element);

    return (
      style.position === 'fixed' ||
      style.position === 'sticky' ||
      style.transform !== 'none' ||
      style.animationName !== 'none'
    );
  };

  const isDecorative = (element) => {
    const classes = classText(element);
    return DECORATIVE_CLASS_PATTERN.test(classes);
  };

  const meaningfulChildren = (container) => {
    return Array.from(container.children).filter((child) => {
      if (!isHTMLElement(child)) return false;
      if (isExcluded(child) || isDecorative(child)) return false;
      if (!isVisibleInLayout(child)) return false;
      return !['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT'].includes(child.tagName);
    });
  };

  const findAutomaticSectionTargets = (section) => {
    if (!isHTMLElement(section) || isExcluded(section)) return [];

    const sectionClasses = classText(section);
    if (NATIVE_MOTION_SECTION_PATTERN.test(sectionClasses)) return [];

    let container = section;

    /*
      Shopify sections often use one or two structural wrappers before the
      meaningful content columns. Walk through those wrappers, then animate
      only the high-level blocks—not every paragraph, icon, or image.
    */
    for (let depth = 0; depth < 3; depth += 1) {
      const children = meaningfulChildren(container);

      if (
        children.length === 1 &&
        (children[0].tagName === 'SECTION' || STRUCTURAL_CLASS_PATTERN.test(classText(children[0])))
      ) {
        container = children[0];
        continue;
      }

      if (children.length >= 2 && children.length <= 8) {
        return children;
      }

      const structuralChildren = children.filter((child) =>
        STRUCTURAL_CLASS_PATTERN.test(classText(child))
      );

      if (structuralChildren.length === 1) {
        container = structuralChildren[0];
        continue;
      }

      break;
    }

    /* Conservative fallback for unusual section structures. */
    return Array.from(
      section.querySelectorAll(
        ':scope h1, :scope h2, :scope h3, :scope .rte, :scope figure, :scope [data-rf-motion-block]'
      )
    ).slice(0, 6);
  };

  const revealElement = (element) => {
    if (!isHTMLElement(element) || element.classList.contains('is-rf-visible')) return;

    element.classList.add('is-rf-visible');
    observer?.unobserve(element);
  };

  const ensureObserver = () => {
    if (observer || reduceMotionQuery.matches) return observer;

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            revealElement(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -8% 0px'
      }
    );

    return observer;
  };

  const prepareRevealElement = (element, index = 0) => {
    if (!isHTMLElement(element)) return false;
    if (element.hasAttribute('data-rf-motion-ready')) return false;
    if (isExcluded(element) || isDecorative(element)) return false;
    if (!isVisibleInLayout(element) || hasUnsafeNativeMotion(element)) return false;

    const computedStyle = window.getComputedStyle(element);
    const classes = classText(element);
    const variant = MEDIA_CLASS_PATTERN.test(classes) ? 'media' : 'up';
    const delay = Math.min(index * 70, 280);

    element.setAttribute('data-rf-motion-ready', 'true');
    element.setAttribute('data-rf-reveal', variant);
    element.style.setProperty('--rf-reveal-delay', `${delay}ms`);
    element.style.setProperty('--rf-final-opacity', computedStyle.opacity || '1');

    return true;
  };

  const enhanceButtons = (scope) => {
    const selector = [
      'a.button',
      'button.button',
      '.shopify-payment-button__button',
      '.rfp-cta',
      '.rf-view',
      '[data-rf-motion-button]'
    ].join(',');

    scope.querySelectorAll(selector).forEach((element) => {
      if (!isHTMLElement(element) || isExcluded(element)) return;
      if (hasUnsafeNativeMotion(element)) return;
      element.setAttribute('data-rf-motion-button', '');
    });
  };

  const enhanceCards = (scope) => {
    /*
      Horizon already owns its product-card hover setting. Only cards that are
      explicitly opted in receive the RAREFORM treatment, preventing two hover
      systems from fighting each other.
    */
    const selector = '[data-rf-motion-card]';

    scope.querySelectorAll(selector).forEach((element) => {
      if (!isHTMLElement(element) || isExcluded(element)) return;
      if (hasUnsafeNativeMotion(element)) return;
      element.setAttribute('data-rf-motion-card', '');
    });
  };

  const initializeScope = (scope = document) => {
    const main = document.querySelector('#MainContent');
    if (!main) return;

    const designMode = documentElement.classList.contains('shopify-design-mode');
    const templateName = main.dataset.template || '';
    const automaticHomepageMotion = templateName.startsWith('index');
    const preparedElements = [];

    enhanceButtons(scope);
    enhanceCards(scope);

    /* Explicit elements work on every page. */
    scope.querySelectorAll('[data-rf-reveal]').forEach((element, index) => {
      if (prepareRevealElement(element, index)) preparedElements.push(element);
    });

    /* Phase 1: automatic high-level reveals are limited to the homepage. */
    if (automaticHomepageMotion) {
      const sectionScope = scope === document ? main : scope;
      const sections = sectionScope.matches?.('.shopify-section')
        ? [sectionScope]
        : Array.from(sectionScope.querySelectorAll('.shopify-section'));

      sections.forEach((section) => {
        findAutomaticSectionTargets(section).forEach((element, index) => {
          if (prepareRevealElement(element, index)) preparedElements.push(element);
        });
      });
    }

    if (designMode || reduceMotionQuery.matches) {
      documentElement.classList.add('rf-motion-reduced');
      preparedElements.forEach(revealElement);
      return;
    }

    const motionObserver = ensureObserver();
    preparedElements.forEach((element) => motionObserver?.observe(element));

    /* Elements already in the initial viewport become the page entrance. */
    window.requestAnimationFrame(() => {
      preparedElements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight * 0.9) {
          revealElement(element);
        }
      });
    });
  };

  const initialize = () => {
    initializeScope(document);
    documentElement.classList.add('rf-motion-ready');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  /* Keep Shopify theme-editor section reloads usable. */
  document.addEventListener('shopify:section:load', (event) => {
    initializeScope(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    event.target.querySelectorAll('[data-rf-reveal]').forEach(revealElement);
  });
})();
